/**
 * SSO session-continuity storage.
 *
 * NOTE: this module deliberately stores NO tokens and NO PII —
 * only a Cognito IdP *name* (already a public URL segment: /saml/<name>), a
 * boolean "sso enforced" hint, and three timestamps. Cognito/Amplify remains
 * the sole owner of JWTs, and no Amplify-internal key (e.g.
 * amplify-signin-with-hostedUI) is ever read or written.
 *
 * Why it exists: for a hosted-UI (SAML) user with an expired refresh token,
 * Amplify's cleanUpInvalidSession performs a FULL BROWSER REDIRECT to the
 * Cognito hosted-UI /logout endpoint, destroying the /saml/<provider> URL the
 * user arrived on. These helpers let the app remember where it was going and
 * resume once the browser lands back on /logout.
 *
 * Every storage access — including reads — is wrapped in try/catch: Safari
 * private mode and storage-disabled iframes throw on *accessing*
 * window.sessionStorage, not only on setItem.
 */

import { isAmplify, isLocalhost } from "../utils";

// ---- host gate ----------------------------------------------------------
/**
 * True on Amplify PR previews (*.amplifyapp.com) and localhost, where the
 * automatic SSO redirect is switched off and the password form always shows.
 *
 * There is no real sign-in on those hosts: developers paste a production
 * Cognito session into localStorage. When that session belongs to an SSO user,
 * the enforced/provider hints get persisted, and once it expires AuthPage would
 * hand the browser to Cognito with the preview host as redirect_uri. Cognito
 * rejects it on its own error page and never redirects back, so the
 * cognitoHostedUI_failure veto can never fire and the developer never gets a
 * login page to paste a fresh session into.
 *
 * Deliberately NOT isDevEnv: app.dev.seemplicity.io keeps the SSO behaviour so
 * the feature stays testable there. Gated on the read side only, so previews
 * already holding stale hints are fixed without touching storage.
 */
export const isSsoAutoRedirectDisabled = (): boolean =>
  isAmplify || isLocalhost;

// ---- storage keys -------------------------------------------------------
/** sessionStorage. Provider + deep link captured from a /saml/:provider URL. */
export const SS_SSO_PENDING_PROVIDER_KEY = "sso_pending_provider";
/**
 * localStorage — NOT sessionStorage (CWE-613). Set by a deliberate logout() so
 * the login page never auto-re-logins. It has to outlive the tab: the enforced
 * flag and last provider are localStorage, so a session-scoped veto would let a
 * NEW tab on / silently sign the user back in through a still-live IdP session
 * (there is no SAML SLO). Cleared ONLY by a successful authentication or by an
 * explicit "Continue with SSO" click.
 */
export const LS_SSO_MANUAL_LOGOUT_KEY = "sso_manual_logout";
/** sessionStorage. Epoch ms of the last IdP sign-in failure. */
export const SS_SSO_FAILURE_AT_KEY = "sso_failure_at";
/** sessionStorage. Epoch ms of the last AUTOMATIC redirect to /saml/*. */
export const SS_SSO_AUTO_REDIRECT_AT_KEY = "sso_auto_redirect_at";
/** localStorage. The Cognito IdP name read from the user's OWN id token. */
export const LS_SSO_LAST_PROVIDER_KEY = "sso_last_provider";
/** localStorage. "1" when the user's HOME org has password_enabled === false. */
export const LS_SSO_ENFORCED_KEY = "sso_enforced";
/** localStorage. Epoch ms of the last write to either hint above (retention). */
export const LS_SSO_HINTS_AT_KEY = "sso_hints_at";
/**
 * sessionStorage. Post-login deep link. PRE-EXISTING key — the literal value
 * must stay "redirect": AuthPage, ProtectedRoute and AuthSAML all already use it.
 */
export const SS_POST_LOGIN_REDIRECT_KEY = "redirect";

// ---- query params -------------------------------------------------------
/** Pre-existing deep-link param: /saml/<provider>?redirect=/findings/1. */
export const SEARCH_PARAM_REDIRECT = "redirect";
/** Escape hatch: ?sso=off reveals the password form in an enforced org. */
export const SEARCH_PARAM_SSO = "sso";
export const SEARCH_PARAM_SSO_OFF_VALUE = "off";

// ---- tunables -----------------------------------------------------------
/** A captured /saml entry is only worth resuming for one hosted-UI round trip. */
export const SSO_PENDING_TTL_MS = 60_000;
/** Hard stop on the resume loop if the bootstrap keeps redirecting away. */
export const SSO_PENDING_MAX_RESUME_ATTEMPTS = 2;
/** At most one AUTOMATIC redirect to the IdP per this window. */
export const SSO_AUTO_REDIRECT_COOLDOWN_MS = 30_000;
/** How long a recorded IdP failure suppresses automatic redirects. */
export const SSO_FAILURE_TTL_MS = 5 * 60_000;
/**
 * Retention bound on the two localStorage hints. They are a convenience, not a
 * security control, so they expire rather than living in the browser forever.
 */
export const SSO_HINTS_TTL_MS = 30 * 24 * 60 * 60_000;

/** Stored value of the two boolean-ish markers (enforced / manual logout). */
const FLAG_SET_VALUE = "1";

/** Cognito's identity-provider-name rule: letters, marks, symbols, numbers,
 * punctuation — no whitespace and no control characters. */
const PROVIDER_NAME_PATTERN = /^[\p{L}\p{M}\p{S}\p{N}\p{P}]+$/u;
/** Cognito caps identity provider names at 32 characters. */
export const PROVIDER_NAME_MAX_LENGTH = 32;

/** Matches exactly the route AppRouter serves: <Route path="/saml/:provider?">. */
const SAML_PATH_PATTERN = /^\/saml\/([^/?#]+)\/?$/;

type StorageKind = "session" | "local";

const getStorage = (kind: StorageKind): Storage =>
  kind === "session" ? window.sessionStorage : window.localStorage;

const readItem = (kind: StorageKind, key: string): string | null => {
  try {
    return getStorage(kind).getItem(key);
  } catch {
    return null;
  }
};

const writeItem = (kind: StorageKind, key: string, value: string): void => {
  try {
    getStorage(kind).setItem(key, value);
  } catch {
    // Storage unavailable (private mode / quota / sandboxed iframe). SSO
    // continuity is a best-effort optimisation, never a correctness
    // requirement — the user simply gets the ordinary login page.
  }
};

const removeItem = (kind: StorageKind, key: string): void => {
  try {
    getStorage(kind).removeItem(key);
  } catch {
    // See writeItem.
  }
};

// ---- query-param helpers ------------------------------------------------
const readSearchParam = (
  search: string | null | undefined,
  param: string
): string | null => {
  try {
    return new URLSearchParams(search ?? "").get(param);
  } catch {
    return null;
  }
};

/** Matches `redirect=` at the start of the search or after a `?`/`&`. */
const REDIRECT_PARAM_PATTERN = new RegExp(
  `(?:^|[?&])${SEARCH_PARAM_REDIRECT}=`
);

/**
 * The `?redirect=` value of a location search string, or null.
 *
 * Everything after the first `redirect=` is the value, which is the legacy
 * contract: `?redirect=/findings?a=1&b=2` yields the whole `/findings?a=1&b=2`.
 * URLSearchParams would truncate that at the `&` and silently drop half of a
 * hand-built or IdP-relayed deep link.
 *
 * Trade-off: a parameter placed AFTER `redirect=` becomes part of the redirect.
 * That is the pre-existing behaviour, and in-product links never append
 * anything after it. The result is still validated by isSafeRedirectPath.
 */
export const readRedirectParam = (search?: string | null): string | null => {
  const raw = search ?? "";
  const match = REDIRECT_PARAM_PATTERN.exec(raw);
  if (!match) {
    return null;
  }
  const value = raw.slice(match.index + match[0].length);
  if (!value) {
    return null;
  }
  try {
    return decodeURIComponent(value);
  } catch {
    // Malformed percent-encoding: fall back to the raw value, exactly as the
    // original split("?redirect=") did.
    return value;
  }
};

/**
 * True when `?sso=off` is present. The enforced flag is only a client-side
 * hint, so a shared browser needs a documented way back to the password form.
 */
export const isSsoDisabledByQuery = (search?: string | null): boolean =>
  readSearchParam(search, SEARCH_PARAM_SSO) === SEARCH_PARAM_SSO_OFF_VALUE;

// ---- open-redirect hardening -------------------------------------------
/**
 * True only for a same-origin RELATIVE path, e.g. "/findings/1?x=1".
 * Rejects "//evil.com", "/\evil.com", "https://evil.com", "javascript:…",
 * "findings/1", "" and anything that resolves off-origin.
 *
 * `new URL()` is what makes the backslash case safe: the WHATWG parser
 * normalises "\" to "/" for special schemes, so "/\evil.com" resolves to
 * origin https://evil.com and is rejected here.
 */
export const isSafeRedirectPath = (value?: string | null): value is string => {
  if (!value || !value.startsWith("/") || value.startsWith("//")) {
    return false;
  }
  try {
    return (
      new URL(value, window.location.origin).origin === window.location.origin
    );
  } catch {
    return false;
  }
};

/**
 * True for a plausible Cognito identity provider name: 1-32 chars of letters,
 * marks, symbols, numbers or punctuation. Rejects whitespace, control
 * characters and over-long values, so a hostile /saml/<segment> can never be
 * persisted or replayed into a federatedSignIn call.
 */
export const isValidProviderName = (value?: string | null): value is string => {
  if (!value || value.length > PROVIDER_NAME_MAX_LENGTH) {
    return false;
  }
  return PROVIDER_NAME_PATTERN.test(value);
};

// ---- pending /saml capture + resume -------------------------------------
export interface PendingSamlProvider {
  provider: string;
  /** Validated same-origin relative path, or undefined. */
  redirect?: string;
  expiresAt: number;
  attempts: number;
}

const parsePendingEntry = (raw: string | null): PendingSamlProvider | null => {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<PendingSamlProvider> | null;
    if (!parsed || typeof parsed.provider !== "string" || !parsed.provider) {
      return null;
    }
    return {
      provider: parsed.provider,
      // Re-validate on read: the entry may predate a validator change, or have
      // been hand-edited in devtools.
      redirect: isSafeRedirectPath(parsed.redirect)
        ? parsed.redirect
        : undefined,
      expiresAt: typeof parsed.expiresAt === "number" ? parsed.expiresAt : 0,
      attempts: typeof parsed.attempts === "number" ? parsed.attempts : 0,
    };
  } catch {
    return null;
  }
};

export const clearPendingSamlProvider = (): void => {
  removeItem("session", SS_SSO_PENDING_PROVIDER_KEY);
};

/**
 * Called from src/index.tsx BEFORE Amplify.configure(). If the tab was opened
 * on /saml/<provider>, remember it so the flow can be resumed after Amplify's
 * hosted-UI logout redirect throws the URL away. No-op for every other URL.
 */
export const captureSsoEntry = (
  pathname: string,
  search: string,
  now: number = Date.now()
): void => {
  const match = SAML_PATH_PATTERN.exec(pathname ?? "");
  if (!match) {
    return;
  }
  let provider = match[1];
  try {
    provider = decodeURIComponent(provider);
  } catch {
    // Not valid percent-encoding — keep the raw segment, which is what the
    // /saml route param would have carried anyway.
  }
  // Validate the decoded segment before anything is written.
  if (!isValidProviderName(provider)) {
    return;
  }
  const redirectParam = readRedirectParam(search);
  const entry: PendingSamlProvider = {
    provider,
    redirect: isSafeRedirectPath(redirectParam) ? redirectParam : undefined,
    expiresAt: now + SSO_PENDING_TTL_MS,
    attempts: 0,
  };
  writeItem("session", SS_SSO_PENDING_PROVIDER_KEY, JSON.stringify(entry));
};

/**
 * Reads the entry without consuming it. Returns null (and clears the stored
 * value) when it is malformed, expired, or out of resume attempts.
 */
export const peekPendingSamlProvider = (
  now: number = Date.now()
): PendingSamlProvider | null => {
  const entry = parsePendingEntry(
    readItem("session", SS_SSO_PENDING_PROVIDER_KEY)
  );
  if (
    !entry ||
    entry.expiresAt <= now ||
    entry.attempts >= SSO_PENDING_MAX_RESUME_ATTEMPTS
  ) {
    clearPendingSamlProvider();
    return null;
  }
  return entry;
};

/**
 * Increments `attempts`. Called by AuthPage immediately before history.replace
 * so a bootstrap that keeps redirecting to the hosted UI cannot loop forever.
 */
export const markPendingSamlResumeAttempt = (
  now: number = Date.now()
): void => {
  const entry = peekPendingSamlProvider(now);
  if (!entry) {
    return;
  }
  writeItem(
    "session",
    SS_SSO_PENDING_PROVIDER_KEY,
    JSON.stringify({ ...entry, attempts: entry.attempts + 1 })
  );
};

/** Reads AND removes the entry. Called by AuthSAML in BOTH branches. */
export const consumePendingSamlProvider = (
  now: number = Date.now()
): PendingSamlProvider | null => {
  const entry = peekPendingSamlProvider(now);
  clearPendingSamlProvider();
  return entry;
};

// ---- retention bound on the localStorage hints -------------------------
/** The three keys that expire together as one unit. */
const HINT_KEYS = [
  LS_SSO_LAST_PROVIDER_KEY,
  LS_SSO_ENFORCED_KEY,
  LS_SSO_HINTS_AT_KEY,
] as const;

/** Stamps the hints as written now. Called only when a hint VALUE is stored. */
const touchHints = (now: number = Date.now()): void => {
  writeItem("local", LS_SSO_HINTS_AT_KEY, String(now));
};

/**
 * Called after removing a hint: if neither hint is left there is nothing to
 * expire, so the stamp goes too. A password-only customer therefore ends up
 * with zero sso_* keys in localStorage rather than a lone orphan timestamp.
 */
const dropStampWhenNoHintsLeft = (): void => {
  const hasHint =
    !!readItem("local", LS_SSO_LAST_PROVIDER_KEY) ||
    !!readItem("local", LS_SSO_ENFORCED_KEY);
  if (!hasHint) {
    removeItem("local", LS_SSO_HINTS_AT_KEY);
  }
};

/**
 * Removes all three hint keys once they are older than SSO_HINTS_TTL_MS, and
 * reports whether it did so the readers below can answer "absent" in the same
 * call. A hint with no timestamp is dropped too: its age is unknowable, and
 * keeping it would mean the unbounded lifetime this bound exists to prevent.
 */
const purgeStaleHints = (now: number = Date.now()): boolean => {
  const rawWrittenAt = readItem("local", LS_SSO_HINTS_AT_KEY);
  const hasAnyHint =
    !!rawWrittenAt ||
    !!readItem("local", LS_SSO_LAST_PROVIDER_KEY) ||
    !!readItem("local", LS_SSO_ENFORCED_KEY);
  if (!hasAnyHint) {
    return false;
  }
  const writtenAt = rawWrittenAt ? Number(rawWrittenAt) : NaN;
  const isFresh =
    Number.isFinite(writtenAt) && now - writtenAt <= SSO_HINTS_TTL_MS;
  if (isFresh) {
    return false;
  }
  HINT_KEYS.forEach((key) => removeItem("local", key));
  return true;
};

// ---- the user's own IdP -------------------------------------------------
export const getLastSsoProvider = (now: number = Date.now()): string | null => {
  if (purgeStaleHints(now)) {
    return null;
  }
  const stored = readItem("local", LS_SSO_LAST_PROVIDER_KEY);
  if (!isValidProviderName(stored)) {
    // A hand-edited or corrupted value counts as absent AND is dropped:
    // reporting it would give the login page an SSO-only layout whose button
    // cannot build a /saml path, i.e. a dead end.
    removeItem("local", LS_SSO_LAST_PROVIDER_KEY);
    dropStampWhenNoHintsLeft();
    return null;
  }
  return stored;
};

export const setLastSsoProvider = (
  provider: string,
  now: number = Date.now()
): void => {
  if (!isValidProviderName(provider)) {
    return;
  }
  writeItem("local", LS_SSO_LAST_PROVIDER_KEY, provider);
  touchHints(now);
};

/** The only slice of the Cognito id token payload this module reads. */
export interface SsoIdTokenPayload {
  identities?: Array<{ providerName?: string }>;
}

/**
 * `identities[0].providerName` is the user's OWN Cognito IdP name. NEVER derive
 * it from the organization object: during CNC impersonation
 * chooseOrganizationAndAccount receives the impersonated customer's org.
 *
 * The last login method wins — a password login clears any stored provider,
 * so a shared browser never offers a previous user's IdP.
 */
export const persistLastSsoProviderFromIdToken = (
  idTokenPayload?: SsoIdTokenPayload | null
): void => {
  const providerName = idTokenPayload?.identities?.[0]?.providerName;
  // An unusable value is treated exactly like a password login: clear, never
  // leave a previous user's provider behind.
  if (!isValidProviderName(providerName)) {
    removeItem("local", LS_SSO_LAST_PROVIDER_KEY);
    dropStampWhenNoHintsLeft();
    return;
  }
  setLastSsoProvider(providerName);
};

/**
 * Every hint update a successful authentication owes: remember (or clear) the
 * user's own IdP, and lift both vetoes. Grouped here so the contract is
 * testable without mounting AuthProvider.
 */
export const recordSuccessfulAuthentication = (
  idTokenPayload?: SsoIdTokenPayload | null
): void => {
  persistLastSsoProviderFromIdToken(idTokenPayload);
  clearSsoFailure();
  clearManualLogoutMarker();
};

// ---- "SSO enforced" for the HOME org ------------------------------------
export const isSsoEnforced = (now: number = Date.now()): boolean => {
  if (purgeStaleHints(now)) {
    return false;
  }
  return readItem("local", LS_SSO_ENFORCED_KEY) === FLAG_SET_VALUE;
};

export const setSsoEnforced = (
  enforced: boolean,
  now: number = Date.now()
): void => {
  if (enforced) {
    writeItem("local", LS_SSO_ENFORCED_KEY, FLAG_SET_VALUE);
    touchHints(now);
    return;
  }
  // A removal is not a write: it must not extend the retention window.
  removeItem("local", LS_SSO_ENFORCED_KEY);
  dropStampWhenNoHintsLeft();
};

/**
 * Persist/remove the flag ONLY for the user's own org — when an
 * impersonation id is present the organization belongs to the impersonated
 * customer and must not influence this browser's login behaviour.
 * Deliberately NOT cleared by clearOrgAndAccount(): it has to survive logout.
 */
export const persistSsoEnforcementForHomeOrg = (
  organization: { password_enabled?: boolean },
  impersonation?: { id?: string } | null
): void => {
  if (impersonation?.id) {
    return;
  }
  setSsoEnforced(organization?.password_enabled === false);
};

// ---- manual logout marker -----------------------------------------------
export const markManualLogout = (): void => {
  writeItem("local", LS_SSO_MANUAL_LOGOUT_KEY, FLAG_SET_VALUE);
};

/** Why logout() was called. Absent/`{}` ⇒ a deliberate, user-initiated logout. */
export interface LogoutIntent {
  /**
   * false ⇒ the platform logged the user out for them (an expired session
   * surfacing as 401/403). Such a logout must NOT be tagged "manual", or
   * AuthPage would veto the SSO auto-redirect that expiry is supposed to start.
   */
  userInitiated?: boolean;
}

/**
 * Writes the manual-logout marker only for a deliberate logout. Defaults to
 * `true` so a caller that passes nothing — including a DOM handler invoked as
 * `onClick={logout}`, whose event has no `userInitiated` — stays on the safe
 * "never silently re-login" side. Only the 401/403 paths opt out explicitly.
 */
export const markLogoutIntent = (options?: LogoutIntent | null): void => {
  if (options?.userInitiated ?? true) {
    markManualLogout();
  }
};

/**
 * Reads WITHOUT clearing. True ⇒ show the login page, never auto-redirect.
 * Read-and-clear would disarm the veto on first read, letting the next
 * navigation to / bounce straight back into a still-live IdP session.
 */
export const hasManualLogoutMarker = (): boolean =>
  readItem("local", LS_SSO_MANUAL_LOGOUT_KEY) === FLAG_SET_VALUE;

/**
 * The ONLY way the veto is lifted: a successful authentication
 * (populateAuthentication) or an explicit "Continue with SSO" click.
 */
export const clearManualLogoutMarker = (): void => {
  removeItem("local", LS_SSO_MANUAL_LOGOUT_KEY);
};

// ---- IdP failure marker -------------------------------------------------
const readTimestamp = (kind: StorageKind, key: string): number | null => {
  const raw = readItem(kind, key);
  if (!raw) {
    return null;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

export const recordSsoFailure = (now: number = Date.now()): void => {
  writeItem("session", SS_SSO_FAILURE_AT_KEY, String(now));
};

export const hasRecentSsoFailure = (now: number = Date.now()): boolean => {
  const failedAt = readTimestamp("session", SS_SSO_FAILURE_AT_KEY);
  if (failedAt === null) {
    return false;
  }
  return now - failedAt < SSO_FAILURE_TTL_MS;
};

export const clearSsoFailure = (): void => {
  removeItem("session", SS_SSO_FAILURE_AT_KEY);
};

// ---- automatic-redirect rate guard --------------------------------------
export const canAutoRedirectToSso = (now: number = Date.now()): boolean => {
  const lastAt = readTimestamp("session", SS_SSO_AUTO_REDIRECT_AT_KEY);
  if (lastAt === null) {
    return true;
  }
  return now - lastAt >= SSO_AUTO_REDIRECT_COOLDOWN_MS;
};

export const markAutoRedirectToSso = (now: number = Date.now()): void => {
  writeItem("session", SS_SSO_AUTO_REDIRECT_AT_KEY, String(now));
};

// ---- post-login deep link -----------------------------------------------
/** Writes only when isSafeRedirectPath(path); otherwise removes the key. */
export const setPostLoginRedirect = (path?: string | null): void => {
  if (isSafeRedirectPath(path)) {
    writeItem("session", SS_POST_LOGIN_REDIRECT_KEY, path);
    return;
  }
  removeItem("session", SS_POST_LOGIN_REDIRECT_KEY);
};

/** Reads AND removes; returns null unless the stored value is still safe. */
export const consumePostLoginRedirect = (): string | null => {
  const stored = readItem("session", SS_POST_LOGIN_REDIRECT_KEY);
  removeItem("session", SS_POST_LOGIN_REDIRECT_KEY);
  return isSafeRedirectPath(stored) ? stored : null;
};

/** Drops a stored deep link without reading it — used by the /logout cleanup. */
export const clearPostLoginRedirect = (): void => {
  removeItem("session", SS_POST_LOGIN_REDIRECT_KEY);
};

// ---- path builder -------------------------------------------------------
/**
 * `/saml/<encoded provider><search>` — `search` is passed through verbatim.
 *
 * @param provider Must satisfy isValidProviderName(); anything else yields
 *   null so a corrupted stored hint cannot be turned into a navigation target.
 * @returns The path, or null when the provider is not a usable IdP name.
 */
export const buildSamlPath = (provider: string, search = ""): string | null => {
  if (!isValidProviderName(provider)) {
    return null;
  }
  return `/saml/${encodeURIComponent(provider)}${search}`;
};

/**
 * True while the tab sits on the /saml/<provider> handoff route. AuthPage uses
 * this to avoid painting the password form mid-handoff — derived from the
 * location, so no state or ref is needed during render.
 */
export const isSamlPath = (pathname?: string | null): boolean =>
  SAML_PATH_PATTERN.test(pathname ?? "");
